import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableActionButton,
  ResponsiveTable,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Zap,
  Plus,
  Edit,
  Trash2,
  Search,
  Send,
  Eye,
  Pause,
  Activity,
  AlertTriangle,
  Clock,
  ChevronDown,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { sanitizeEmailContent } from "@/lib/sanitize";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { formatDatePretty, formatDateOnlySAST } from "@/lib/dateUtils";

// Campaign form schema
const campaignFormSchema = z
  .object({
    name: z.string().min(1, "Campaign name is required"),
    description: z.string().optional(),
    criteriaIds: z.array(z.number()).optional(), // Multi-criteria support
    targetSegmentId: z.number().optional(),
    // Optional at the field level; we enforce channel-specific requirements below
    templateId: z.string().nullable().optional(), // AWS SES template name
    channel: z.enum(["email", "sms", "in_app"]).default("email"),
    smsTemplateId: z.number().optional(),
    status: z.enum(["draft", "scheduled"]).default("draft"),
    throttlePerMin: z.number().min(1).max(50000).default(25000),
    cronSchedule: z.string().optional(),
    // Optional one-off scheduled date/time (ISO string from datetime-local input)
    scheduledAt: z.string().nullable().optional(),
    utm: z
      .object({
        source: z.string().optional(),
        medium: z.string().optional(),
        campaign: z.string().optional(),
        term: z.string().optional(),
        content: z.string().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const channel = data.channel || "email";
    const hasCriteria =
      Array.isArray(data.criteriaIds) && data.criteriaIds.length > 0;
    const hasSegment = typeof data.targetSegmentId === "number";

    if (!hasCriteria && !hasSegment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one target criteria or a user segment",
        path: ["criteriaIds"],
      });
    }

    if (channel === "email") {
      if (!data.templateId || data.templateId.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Template is required for email campaigns",
          path: ["templateId"],
        });
      }
      return;
    }

    // For SMS and In-App campaigns, require SMS template instead
    if ((channel === "sms" || channel === "in_app") && !data.smsTemplateId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "SMS template is required for SMS and in-app campaigns",
        path: ["smsTemplateId"],
      });
    }
  });

type CampaignForm = z.infer<typeof campaignFormSchema>;

interface EmailCampaign {
  id: number;
  name: string;
  description?: string;
  criteriaId?: number | null;
  criteriaIds?: number[];
  criteriaNames?: string[];
  targetSegmentId?: number | null;
  templateId: string; // AWS SES template name
  channel?: string;
  smsTemplateId?: number | null;
  status: string;
  throttlePerMin?: number;
  cronSchedule?: string;
  sentAt?: string;
  lastSentAt?: string;
  recipientCount?: number;
  utm?: any;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  templateName?: string;
  creatorName?: string;
  criteriaName?: string;
}

export default function CRMCampaignsTab() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] =
    useState<EmailCampaign | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch campaigns
  const {
    data: campaignsData,
    isLoading: campaignsLoading,
    refetch: refetchCampaigns,
  } = useQuery({
    queryKey: [
      "/api/admin/crm/campaigns",
      currentPage,
      selectedStatus,
      searchTerm,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: "20",
      });
      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (searchTerm) params.append("search", searchTerm);

      const response = await apiRequest(
        "GET",
        `/api/admin/crm/campaigns?${params}`
      );
      return response.json();
    },
  });

  // Fetch AWS SES templates for dropdowns
  const { data: templatesData } = useQuery({
    queryKey: ["/api/admin/crm/ses-templates"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/crm/ses-templates");
      return response.json();
    },
  });

  // Fetch user criteria for targeting
  const { data: criteriaData } = useQuery({
    queryKey: ["/api/admin/crm/criteria", "active"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/crm/criteria?limit=100&active=true"
      );
      return response.json();
    },
  });

  const campaigns = campaignsData?.campaigns || [];
  const pagination = campaignsData?.pagination || {};
  const templates = templatesData?.templates || []; // AWS SES templates
  const criteria = criteriaData?.criteria || [];

  // Fetch high-level campaign metrics (status, health, runs today)
  const { data: campaignMetrics } = useQuery({
    queryKey: ["/api/admin/crm/campaigns/metrics"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/crm/campaigns/metrics"
      );
      return response.json();
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const metrics = campaignMetrics?.data || {};
  const statusMetrics = metrics.status || {};
  const healthMetrics = metrics.health || {};
  const runsMetrics = metrics.runs || {};
  // Prefer calculating active/scheduled campaigns from the current page data
  // so the card always reflects what admins see in the table.
  const activeScheduledCount =
    campaigns.filter(
      (c) => c.status === "scheduled" || c.status === "sending"
    ).length || statusMetrics.activeScheduled || 0;
  const campaignsWithErrors24h = healthMetrics.campaignsWithErrors24h ?? 0;
  const runsToday = runsMetrics.runsToday ?? 0;

  // Fetch user segments for segment-based targeting
  const { data: segmentsResponse } = useQuery<{
    success: boolean;
    segments: { id: number; name: string }[];
    count: number;
  }>({
    queryKey: ["/api/v1/segments"],
  });
  const segments = segmentsResponse?.segments || [];

  // Fetch SMS templates for SMS and In-App campaigns
  const { data: smsTemplatesData } = useQuery<any[]>({
    queryKey: ["/api/sms-config/templates"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/sms-config/templates");
      return response.json();
    },
  });
  const smsTemplates = smsTemplatesData || [];

  // Template preview state (reuses AWS SES preview API)
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any | null>(null);

  const previewAwsTemplateMutation = useMutation({
    mutationFn: async ({
      templateName,
      variables,
    }: {
      templateName: string;
      variables: Record<string, string>;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/crm/ses-templates/${encodeURIComponent(
          templateName
        )}/preview`,
        { variables }
      );
      return response.json();
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setIsPreviewDialogOpen(true);
    },
    onError: (error: any) => {
      toast({
        title: "Error previewing template",
        description: error.message || "Failed to preview AWS SES template",
        variant: "destructive",
      });
    },
  });

  // Create form
  const createForm = useForm<CampaignForm>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      name: "",
      description: "",
      criteriaIds: [],
      targetSegmentId: undefined,
      templateId: undefined,
      channel: "email",
      smsTemplateId: undefined,
      status: "draft",
      throttlePerMin: 25000,
      cronSchedule: "",
      scheduledAt: null,
      utm: {},
    },
  });

  // Edit form
  const editForm = useForm<CampaignForm>({
    resolver: zodResolver(campaignFormSchema),
  });

  // Create campaign mutation
  const createCampaignMutation = useMutation({
    mutationFn: async (data: CampaignForm) => {
      const response = await apiRequest("POST", "/api/admin/crm/campaigns", {
        body: data,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Campaign created successfully!" });
      setIsCreateDialogOpen(false);
      createForm.reset();
      refetchCampaigns();
    },
    onError: (error: any) => {
      toast({
        title: "Error creating campaign",
        description: error.message || "Failed to create campaign",
        variant: "destructive",
      });
    },
  });

  // Update campaign mutation
  const updateCampaignMutation = useMutation({
    mutationFn: async (data: CampaignForm & { id: number }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/admin/crm/campaigns/${data.id}`,
        {
          body: data,
        }
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Campaign updated successfully!" });
      setIsEditDialogOpen(false);
      editForm.reset();
      refetchCampaigns();
    },
    onError: (error: any) => {
      toast({
        title: "Error updating campaign",
        description: error.message || "Failed to update campaign",
        variant: "destructive",
      });
    },
  });

  // Delete campaign mutation
  const deleteCampaignMutation = useMutation({
    mutationFn: async (campaignId: number) => {
      const response = await apiRequest(
        "DELETE",
        `/api/admin/crm/campaigns/${campaignId}`
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Campaign deleted successfully!" });
      refetchCampaigns();
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting campaign",
        description: error.message || "Failed to delete campaign",
        variant: "destructive",
      });
    },
  });

  // Manual send mutation
  const sendCampaignMutation = useMutation({
    mutationFn: async (campaignId: number) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/crm/campaigns/${campaignId}/trigger`
      );
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Campaign sent successfully!",
        description: data.message || `Sent to ${data.recipientCount} users`,
      });
      refetchCampaigns();
    },
    onError: (error: any) => {
      toast({
        title: "Error sending campaign",
        description: error.message || "Failed to send campaign",
        variant: "destructive",
      });
    },
  });

  // Toggle (pause/resume) campaign mutation
  const toggleCampaignMutation = useMutation({
    mutationFn: async (campaignId: number) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/crm/campaigns/${campaignId}/toggle`
      );
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Campaign status updated",
        description: data.message || "Campaign status changed.",
      });
      refetchCampaigns();
    },
    onError: (error: any) => {
      toast({
        title: "Error updating campaign status",
        description: error.message || "Failed to update campaign status",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (campaign: EmailCampaign) => {
    setSelectedCampaign(campaign);

    // Use criteriaIds from API, fall back to legacy criteriaId for backward compatibility
    const criteriaIds =
      Array.isArray(campaign.criteriaIds) && campaign.criteriaIds.length > 0
        ? campaign.criteriaIds
        : campaign.criteriaId
        ? [campaign.criteriaId]
        : [];

    const formData: CampaignForm = {
      name: campaign.name,
      description: campaign.description || "",
      criteriaIds,
      targetSegmentId: campaign.targetSegmentId ?? undefined,
      templateId: campaign.templateId,
      channel:
        campaign.channel === "sms" || campaign.channel === "in_app"
          ? (campaign.channel as "email" | "sms" | "in_app")
          : "email",
      smsTemplateId: campaign.smsTemplateId || undefined,
      status: campaign.status as "draft" | "scheduled",
      throttlePerMin: campaign.throttlePerMin || 25000,
      cronSchedule: campaign.cronSchedule || "",
      scheduledAt: null,
      utm: campaign.utm || {},
    };

    editForm.reset(formData);
    setIsEditDialogOpen(true);
  };

  const statusFilters = [
    { value: "all", label: "All Statuses" },
    { value: "draft", label: "Draft" },
    { value: "scheduled", label: "Scheduled" },
    { value: "sending", label: "Sending" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="h-6 w-6" />
            Email Campaigns
          </h2>
          <p className="text-gray-600 mt-1">
            Create and manage automated email campaigns
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          data-testid="button-create-campaign"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Active / Scheduled Campaigns
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {activeScheduledCount}
                </p>
              </div>
              <Activity className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Campaigns with send errors (24h)
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {campaignsWithErrors24h}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Campaign runs today
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {runsToday}
                </p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search Campaigns</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="search"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-campaigns"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger data-testid="select-campaign-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusFilters.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Card>
        <CardHeader>
          <CardTitle>Campaigns ({campaigns.length})</CardTitle>
          <CardDescription>
            Manage your email campaigns and track their performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {campaignsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading campaigns...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-8">
              <Zap className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No campaigns found
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || selectedStatus !== "all"
                  ? "No campaigns match your current filters."
                  : "Get started by creating your first email campaign."}
              </p>
              {!searchTerm && selectedStatus === "all" && (
                <Button
                  onClick={() => setIsCreateDialogOpen(true)}
                  data-testid="button-create-first-campaign"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Campaign
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden">
              <ResponsiveTable
                data={campaigns}
                breakpoint="mobile-lg"
                cardTitle={(campaign) => campaign.name}
                cardSubtitle={(campaign) => campaign.description}
                actions={(campaign) => (
                  <>
                    <TableActionButton
                      icon={<Eye className="h-4 w-4" />}
                      label="View Template"
                      onClick={() => {
                        if (!campaign.sesTemplateName) {
                          toast({
                            title: "No AWS template",
                            description:
                              "This campaign is not linked to an AWS SES template.",
                          });
                          return;
                        }
                        previewAwsTemplateMutation.mutate({
                          templateName: campaign.sesTemplateName,
                          variables: {},
                        });
                      }}
                      data-testid={`button-preview-template-${campaign.id}`}
                    />
                    <TableActionButton
                      icon={<Edit className="h-4 w-4" />}
                      label="Edit"
                      onClick={() => handleEdit(campaign)}
                      data-testid={`button-edit-${campaign.id}`}
                    />
                    <TableActionButton
                      icon={<Send className="h-4 w-4" />}
                      label={
                        campaign.status === "completed" ? "Resend" : "Send Now"
                      }
                      onClick={() => sendCampaignMutation.mutate(campaign.id)}
                      disabled={
                        sendCampaignMutation.isPending ||
                        campaign.status === "sending"
                      }
                      data-testid={`button-send-${campaign.id}`}
                    />
                    <TableActionButton
                      icon={<Pause className="h-4 w-4" />}
                      label={
                        campaign.status === "sending"
                          ? "Pause"
                          : "Pause / Resume"
                      }
                      onClick={() => toggleCampaignMutation.mutate(campaign.id)}
                      disabled={toggleCampaignMutation.isPending}
                      data-testid={`button-toggle-${campaign.id}`}
                    />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <TableActionButton
                          icon={<Trash2 className="h-4 w-4" />}
                          label="Delete"
                          variant="destructive"
                          data-testid={`button-delete-${campaign.id}`}
                        />
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{campaign.name}"?
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() =>
                              deleteCampaignMutation.mutate(campaign.id)
                            }
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
                columns={[
                  {
                    key: "name",
                    header: "Name",
                    priority: "high",
                    render: (campaign) => (
                      <div>
                        <div className="font-medium">{campaign.name}</div>
                        {campaign.description && (
                          <div className="text-sm text-gray-500 truncate max-w-[200px]">
                            {campaign.description}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "criteria",
                    header: "Targeting",
                    priority: "medium",
                    render: (campaign) => (
                      <div className="max-w-[200px]">
                        {(() => {
                          const parts: string[] = [];

                          // Use criteriaNames array if available, fall back to legacy criteriaName
                          if (
                            Array.isArray(campaign.criteriaNames) &&
                            campaign.criteriaNames.length > 0
                          ) {
                            if (campaign.criteriaNames.length <= 2) {
                              parts.push(...campaign.criteriaNames);
                            } else {
                              parts.push(
                                `${campaign.criteriaNames.slice(0, 2).join(", ")} +${campaign.criteriaNames.length - 2} more`
                              );
                            }
                          } else if (campaign.criteriaId) {
                            parts.push(
                              campaign.criteriaName ||
                                `Criteria ${campaign.criteriaId}`
                            );
                          }

                          if (campaign.targetSegmentId) {
                            parts.push(`Segment ${campaign.targetSegmentId}`);
                          }

                          return parts.length > 0 ? (
                            <span className="truncate" title={parts.join(", ")}>
                              {parts.join(", ")}
                            </span>
                          ) : (
                            "Not configured"
                          );
                        })()}
                      </div>
                    ),
                  },
                  {
                    key: "template",
                    header: "Template",
                    priority: "low",
                    hideOnMobile: true,
                    render: (campaign) => (
                      <div className="max-w-[150px] truncate">
                        {campaign.templateName ||
                          `Template ${campaign.templateId}`}
                      </div>
                    ),
                  },
                  {
                    key: "channel",
                    header: "Channel",
                    priority: "medium",
                    render: (campaign) => (
                      <Badge variant="outline" className="capitalize">
                        {campaign.channel || "email"}
                      </Badge>
                    ),
                  },
                  {
                    key: "status",
                    header: "Status",
                    priority: "medium",
                    render: (campaign) => (
                      <Badge
                        variant={
                          campaign.status === "completed"
                            ? "default"
                            : campaign.status === "sending"
                            ? "secondary"
                            : campaign.status === "scheduled"
                            ? "outline"
                            : campaign.status === "cancelled"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {campaign.status}
                      </Badge>
                    ),
                  },
                  {
                    key: "schedule",
                    header: "Schedule",
                    priority: "medium",
                    render: (campaign) => (
                      <div className="text-xs">
                        {campaign.cronSchedule &&
                        campaign.cronSchedule.trim() !== "" ? (
                          <div>
                            <Badge variant="outline" className="mb-1">
                              Recurring
                            </Badge>
                            <div className="font-mono text-xs">
                              {campaign.cronSchedule}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="secondary">Manual</Badge>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "recipients",
                    header: "Recipients",
                    priority: "medium",
                    render: (campaign) => campaign.recipientCount || 0,
                  },
                  {
                    key: "created",
                    header: "Created",
                    priority: "low",
                    hideOnMobile: true,
                    render: (campaign) =>
                      formatDateOnlySAST(campaign.createdAt),
                  },
                ]}
              />

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total} campaigns
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={pagination.page <= 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(prev + 1, pagination.totalPages)
                        )
                      }
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Email Campaign</DialogTitle>
            <DialogDescription>
              Create a new email campaign to engage your users
            </DialogDescription>
          </DialogHeader>
          <Form {...createForm}>
            <form
              onSubmit={createForm.handleSubmit((data) =>
                createCampaignMutation.mutate(data)
              )}
              className="space-y-4"
            >
              <FormField
                control={createForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Campaign Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Welcome Series"
                        {...field}
                        data-testid="input-campaign-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Campaign description..."
                        {...field}
                        data-testid="textarea-campaign-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={createForm.control}
                  name="channel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Channel</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger data-testid="select-campaign-channel">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="in_app">In-App</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createForm.control}
                  name="criteriaIds"
                  render={({ field }) => {
                    const selectedIds = field.value || [];
                    const selectedNames = criteria
                      .filter((c: any) => selectedIds.includes(c.id))
                      .map((c: any) => c.name);

                    return (
                      <FormItem>
                        <FormLabel>Target Criteria</FormLabel>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between font-normal"
                                data-testid="select-campaign-criteria"
                              >
                                {selectedIds.length > 0 ? (
                                  <span className="truncate">
                                    {selectedNames.length <= 2
                                      ? selectedNames.join(", ")
                                      : `${selectedNames.slice(0, 2).join(", ")} +${selectedNames.length - 2} more`}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">
                                    Select criteria...
                                  </span>
                                )}
                                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[300px] p-0" align="start">
                              <ScrollArea className="max-h-[300px]">
                                <div className="p-2 space-y-1">
                                  {criteria.map((c: any) => {
                                    const isSelected = selectedIds.includes(c.id);
                                    return (
                                      <div
                                        key={c.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                                        onClick={() => {
                                          const newValue = isSelected
                                            ? selectedIds.filter((id: number) => id !== c.id)
                                            : [...selectedIds, c.id];
                                          field.onChange(newValue);
                                        }}
                                      >
                                        <Checkbox
                                          checked={isSelected}
                                          onCheckedChange={(checked) => {
                                            const newValue = checked
                                              ? [...selectedIds, c.id]
                                              : selectedIds.filter((id: number) => id !== c.id);
                                            field.onChange(newValue);
                                          }}
                                        />
                                        <span className="text-sm">{c.name}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </ScrollArea>
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        {selectedIds.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedNames.map((name: string, idx: number) => (
                              <Badge
                                key={selectedIds[idx]}
                                variant="secondary"
                                className="text-xs"
                              >
                                {name}
                                <X
                                  className="ml-1 h-3 w-3 cursor-pointer"
                                  onClick={() => {
                                    const newValue = selectedIds.filter(
                                      (id: number) => id !== selectedIds[idx]
                                    );
                                    field.onChange(newValue);
                                  }}
                                />
                              </Badge>
                            ))}
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={createForm.control}
                  name="targetSegmentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>User Segment (optional)</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value?.toString() || ""}
                          onValueChange={(value) =>
                            field.onChange(value ? parseInt(value) : undefined)
                          }
                        >
                          <SelectTrigger data-testid="select-campaign-segment">
                            <SelectValue placeholder="Select segment" />
                          </SelectTrigger>
                          <SelectContent>
                            {segments.map((segment: any) => (
                              <SelectItem
                                key={segment.id}
                                value={segment.id.toString()}
                              >
                                {segment.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createForm.control}
                  name="templateId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Template (AWS SES)</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value || ""}
                          onValueChange={(value) => field.onChange(value)}
                          disabled={createForm.watch("channel") !== "email"}
                        >
                          <SelectTrigger data-testid="select-campaign-template">
                            <SelectValue placeholder="Select AWS template" />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map((template: any) => (
                              <SelectItem
                                key={template.templateName}
                                value={template.templateName}
                              >
                                {template.templateName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createForm.control}
                  name="smsTemplateId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SMS / In-App Template</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value ? String(field.value) : ""}
                          onValueChange={(value) =>
                            field.onChange(parseInt(value))
                          }
                          disabled={
                            !["sms", "in_app"].includes(
                              createForm.watch("channel")
                            )
                          }
                        >
                          <SelectTrigger data-testid="select-campaign-sms-template">
                            <SelectValue placeholder="Select SMS template" />
                          </SelectTrigger>
                          <SelectContent>
                            {smsTemplates.map((template: any) => (
                              <SelectItem
                                key={template.id}
                                value={template.id.toString()}
                              >
                                {template.name} ({template.templateKey})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={createForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger data-testid="select-campaign-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createForm.control}
                  name="throttlePerMin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Rate Limit (per minute)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="25000"
                          {...field}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            field.onChange(
                              Number.isFinite(value) ? value : 25000
                            );
                          }}
                          data-testid="input-throttle-rate"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={createForm.control}
                name="cronSchedule"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Cron Schedule (Optional - Leave empty for manual
                      triggering)
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="0 9 * * * (daily at 9 AM)"
                        {...field}
                        data-testid="input-cron-schedule"
                      />
                    </FormControl>
                    <p className="text-sm text-muted-foreground">
                      Enter cron expression (min hour day month weekday).
                      Examples:
                      <br />• <code>0 9 * * *</code> - Daily at 9 AM
                      <br />• <code>0 9 * * 1</code> - Weekly on Monday at 9 AM
                      <br />• <code>0 9 1 * *</code> - Monthly on 1st at 9 AM
                      <br />• <code>0 */2 * * *</code> - Every 2 hours
                      <br />• <code>30 14 * * 5</code> - Every Friday at 2:30 PM
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createForm.control}
                name="scheduledAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>One-off Scheduled Send (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        data-testid="input-oneoff-scheduled-at"
                      />
                    </FormControl>
                    <p className="text-sm text-muted-foreground">
                      Set a single future date/time to send this campaign once.
                      This is in addition to any cron schedule.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <h4 className="font-medium">UTM Parameters (Optional)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={createForm.control}
                    name="utm.source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UTM Source</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="newsletter"
                            {...field}
                            data-testid="input-utm-source"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={createForm.control}
                    name="utm.medium"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UTM Medium</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="email"
                            {...field}
                            data-testid="input-utm-medium"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={createForm.control}
                    name="utm.campaign"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UTM Campaign</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="welcome_series"
                            {...field}
                            data-testid="input-utm-campaign"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={createForm.control}
                    name="utm.term"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UTM Term</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="new_users"
                            {...field}
                            data-testid="input-utm-term"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createCampaignMutation.isPending}
                  data-testid="button-save-campaign"
                >
                  {createCampaignMutation.isPending
                    ? "Creating..."
                    : "Create Campaign"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Campaign Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Campaign</DialogTitle>
            <DialogDescription>Update the campaign details</DialogDescription>
          </DialogHeader>
          <Form {...editForm}>
            <form
              onSubmit={editForm.handleSubmit(
                (data) =>
                  selectedCampaign &&
                  updateCampaignMutation.mutate({
                    ...data,
                    id: selectedCampaign.id,
                  })
              )}
              className="space-y-4"
            >
              <FormField
                control={editForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Campaign Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        data-testid="input-edit-campaign-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        data-testid="textarea-edit-campaign-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="channel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Channel</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger data-testid="select-edit-campaign-channel">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="in_app">In-App</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="criteriaIds"
                  render={({ field }) => {
                    const selectedIds = field.value || [];
                    const selectedNames = criteria
                      .filter((c: any) => selectedIds.includes(c.id))
                      .map((c: any) => c.name);

                    return (
                      <FormItem>
                        <FormLabel>Target Criteria</FormLabel>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between font-normal"
                                data-testid="select-edit-campaign-criteria"
                              >
                                {selectedIds.length > 0 ? (
                                  <span className="truncate">
                                    {selectedNames.length <= 2
                                      ? selectedNames.join(", ")
                                      : `${selectedNames.slice(0, 2).join(", ")} +${selectedNames.length - 2} more`}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">
                                    Select criteria...
                                  </span>
                                )}
                                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[300px] p-0" align="start">
                              <ScrollArea className="max-h-[300px]">
                                <div className="p-2 space-y-1">
                                  {criteria.map((c: any) => {
                                    const isSelected = selectedIds.includes(c.id);
                                    return (
                                      <div
                                        key={c.id}
                                        className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                                        onClick={() => {
                                          const newValue = isSelected
                                            ? selectedIds.filter((id: number) => id !== c.id)
                                            : [...selectedIds, c.id];
                                          field.onChange(newValue);
                                        }}
                                      >
                                        <Checkbox
                                          checked={isSelected}
                                          onCheckedChange={(checked) => {
                                            const newValue = checked
                                              ? [...selectedIds, c.id]
                                              : selectedIds.filter((id: number) => id !== c.id);
                                            field.onChange(newValue);
                                          }}
                                        />
                                        <span className="text-sm">{c.name}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </ScrollArea>
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        {selectedIds.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedNames.map((name: string, idx: number) => (
                              <Badge
                                key={selectedIds[idx]}
                                variant="secondary"
                                className="text-xs"
                              >
                                {name}
                                <X
                                  className="ml-1 h-3 w-3 cursor-pointer"
                                  onClick={() => {
                                    const newValue = selectedIds.filter(
                                      (id: number) => id !== selectedIds[idx]
                                    );
                                    field.onChange(newValue);
                                  }}
                                />
                              </Badge>
                            ))}
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={editForm.control}
                  name="targetSegmentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>User Segment (optional)</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value?.toString() || ""}
                          onValueChange={(value) =>
                            field.onChange(value ? parseInt(value) : undefined)
                          }
                        >
                          <SelectTrigger data-testid="select-edit-campaign-segment">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {segments.map((segment: any) => (
                              <SelectItem
                                key={segment.id}
                                value={segment.id.toString()}
                              >
                                {segment.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="templateId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Template (AWS SES)</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value || ""}
                          onValueChange={(value) => field.onChange(value)}
                          disabled={editForm.watch("channel") !== "email"}
                        >
                          <SelectTrigger data-testid="select-edit-campaign-template">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map((template: any) => (
                              <SelectItem
                                key={template.templateName}
                                value={template.templateName}
                              >
                                {template.templateName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="smsTemplateId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SMS / In-App Template</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value ? String(field.value) : ""}
                          onValueChange={(value) =>
                            field.onChange(parseInt(value))
                          }
                          disabled={
                            !["sms", "in_app"].includes(
                              editForm.watch("channel")
                            )
                          }
                        >
                          <SelectTrigger data-testid="select-edit-campaign-sms-template">
                            <SelectValue placeholder="Select SMS template" />
                          </SelectTrigger>
                          <SelectContent>
                            {smsTemplates.map((template: any) => (
                              <SelectItem
                                key={template.id}
                                value={template.id.toString()}
                              >
                                {template.name} ({template.templateKey})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger data-testid="select-edit-campaign-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="throttlePerMin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Rate Limit (per minute)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="25000"
                          {...field}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            field.onChange(
                              Number.isFinite(value) ? value : 25000
                            );
                          }}
                          data-testid="input-edit-throttle-rate"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={editForm.control}
                name="cronSchedule"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Cron Schedule (Optional - Leave empty for manual
                      triggering)
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="0 9 * * * (daily at 9 AM)"
                        {...field}
                        data-testid="input-edit-cron-schedule"
                      />
                    </FormControl>
                    <p className="text-sm text-muted-foreground">
                      Enter cron expression (min hour day month weekday).
                      Examples:
                      <br />• <code>0 9 * * *</code> - Daily at 9 AM
                      <br />• <code>0 9 * * 1</code> - Weekly on Monday at 9 AM
                      <br />• <code>0 9 1 * *</code> - Monthly on 1st at 9 AM
                      <br />• <code>0 */2 * * *</code> - Every 2 hours
                      <br />• <code>30 14 * * 5</code> - Every Friday at 2:30 PM
                    </p>
                    {selectedCampaign?.lastSentAt && (
                      <p className="text-sm text-green-600">
                        Last sent:{" "}
                        {formatDatePretty(selectedCampaign.lastSentAt)}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="scheduledAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>One-off Scheduled Send (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        data-testid="input-edit-oneoff-scheduled-at"
                      />
                    </FormControl>
                    <p className="text-sm text-muted-foreground">
                      Set a single future date/time to send this campaign once.
                      This is in addition to any cron schedule.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <h4 className="font-medium">UTM Parameters (Optional)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={editForm.control}
                    name="utm.source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UTM Source</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="newsletter"
                            {...field}
                            data-testid="input-edit-utm-source"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="utm.medium"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UTM Medium</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="email"
                            {...field}
                            data-testid="input-edit-utm-medium"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="utm.campaign"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UTM Campaign</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="welcome_series"
                            {...field}
                            data-testid="input-edit-utm-campaign"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="utm.term"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UTM Term</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="new_users"
                            {...field}
                            data-testid="input-edit-utm-term"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateCampaignMutation.isPending}
                  data-testid="button-update-campaign"
                >
                  {updateCampaignMutation.isPending
                    ? "Updating..."
                    : "Update Campaign"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campaign Template Preview</DialogTitle>
            <DialogDescription>
              Rendered view of the AWS SES template assigned to this campaign.
            </DialogDescription>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Subject</Label>
                  <p className="text-sm text-gray-700">{previewData.subject}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Template</Label>
                  <p className="text-sm text-gray-700">
                    {previewData.templateName || selectedCampaign?.templateId}
                  </p>
                </div>
              </div>

              <Tabs defaultValue="html" className="w-full">
                <TabsList>
                  <TabsTrigger value="html">HTML Preview</TabsTrigger>
                  <TabsTrigger value="text">Text Version</TabsTrigger>
                  <TabsTrigger value="code">HTML Code</TabsTrigger>
                </TabsList>

                <TabsContent value="html" className="mt-4">
                  <div className="border rounded-lg p-4 bg-white">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeEmailContent(previewData.htmlBody),
                      }}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="text" className="mt-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <pre className="whitespace-pre-wrap text-sm">
                      {previewData.textBody}
                    </pre>
                  </div>
                </TabsContent>

                <TabsContent value="code" className="mt-4">
                  <div className="border rounded-lg p-4 bg-gray-900 text-green-400">
                    <pre className="text-xs overflow-x-auto">
                      <code>{previewData.htmlBody}</code>
                    </pre>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
